export interface ScoreboardLoan {
  encompass_loan_id: string;
  loan_officer_first_name: string | null;
  loan_officer_last_name: string | null;
  trans_details_loan_amt: number | null;
}
export const LO_ALIASES = new Map<string, string>([['mohammad nasim', 'Tony Nasim']]);

/** How a loan's credited LO was determined (for the audit/detail trail). */
export type CreditSource = 'recon' | 'encompass';

export interface CreditedLo {
  name: string;
  joined: string | null;
  source: CreditSource;
}

/**
 * Authoritative payee for a loan, resolved from a finalized banker recon.
 * (The component only feeds payees from finalized recons.)
 */
export interface ReconPayee {
  userId: string;
  name: string;
}

/**
 * Everything needed to attribute a loan to the right LO. The component builds
 * these maps from banker_recons + users; tests build them inline.
 */
export interface AttributionContext {
  /** encompass_loan_id -> authoritative banker-recon payee */
  payeeByEncompassId: Map<string, ReconPayee>;
  /** users.id -> created_at (ISO) — exact "Joined" for recon-attributed loans */
  joinedByUserId: Map<string, string>;
  /** normalized name -> created_at (ISO) — best-effort "Joined" for the fallback */
  joinedByName: Map<string, string>;
  /** normalized alias -> canonical display name (e.g. "mohammad nasim" -> "Tony Nasim") */
  aliasMap: Map<string, string>;
}

/** Normalized key for matching/grouping an LO display name. */
export function normalizeLoKey(name: string | null | undefined): string {
  return (name || '').replace(/\s+/g, ' ').toLowerCase().trim();
}

/** Collapse internal whitespace and trim — canonical display form of a name. */
function cleanName(name: string | null | undefined): string {
  return (name || '').replace(/\s+/g, ' ').trim();
}

/** Empty attribution context — falls back to the Encompass name with no joined date. */
export function emptyAttribution(): AttributionContext {
  return {
    payeeByEncompassId: new Map(),
    joinedByUserId: new Map(),
    joinedByName: new Map(),
    aliasMap: new Map()
  };
}

/** Fold a known alias to its canonical display name; otherwise return as-is. */
export function applyAlias(name: string, aliasMap: Map<string, string>): string {
  return aliasMap.get(normalizeLoKey(name)) ?? name;
}

/**
 * Resolve the LO who should be credited for a funded loan.
 * Prefers the banker-recon payee (matched on encompass_loan_id); falls back to
 * the Encompass loan officer.
 */
export function resolveCreditedLo(loan: ScoreboardLoan, ctx: AttributionContext): CreditedLo {
  const encompassId = loan.encompass_loan_id || '';
  const payee = encompassId ? ctx.payeeByEncompassId.get(encompassId) : undefined;

  if (payee) {
    const name = cleanName(applyAlias(payee.name, ctx.aliasMap));
    const joined =
      ctx.joinedByUserId.get(payee.userId) ?? ctx.joinedByName.get(normalizeLoKey(name)) ?? null;
    return { name, joined, source: 'recon' };
  }

  const original = [loan.loan_officer_first_name, loan.loan_officer_last_name].filter(Boolean).join(' ') || '-';
  const resolvedName = cleanName(applyAlias(original, ctx.aliasMap));
  const name = resolvedName && resolvedName !== '-' ? resolvedName : 'Unassigned';
  // Try the canonical (aliased) name first, then the original, so an alias never
  // discards a joined date that only the original name could resolve.
  const joined =
    ctx.joinedByName.get(normalizeLoKey(name)) ??
    ctx.joinedByName.get(normalizeLoKey(original)) ??
    null;
  return { name, joined, source: 'encompass' };
}

export function groupScoreboardLoans<TLoan extends ScoreboardLoan>(
  loans: TLoan[], ctx: AttributionContext
) {
  const groups = new Map<string, { displayName: string; loans: TLoan[]; joined: string | null }>();

  loans.forEach((loan) => {
    const credited = resolveCreditedLo(loan, ctx);
    const displayName = credited.name && credited.name !== '-'
      ? credited.name
      : 'Unassigned';
    const key = normalizeLoKey(displayName);
    const bucket = groups.get(key);
    if (bucket) {
      bucket.loans.push(loan);
      // Earliest valid created_at wins — deterministic, independent of order.
      if (credited.joined && (!bucket.joined || credited.joined < bucket.joined)) {
        bucket.joined = credited.joined;
      }
    } else {
      groups.set(key, { displayName, loans: [loan], joined: credited.joined });
    }
  });

  return [...groups.values()];
}
export interface BankerReconRow {
  encompass_loan_id: string | null;
  lo_user_id: string | null;
  lo_name: string | null;
  joined_at: string | null;
  finalized_at: string | null;
}

export interface LOScoreboardAttributionRow {
  record_type: 'user' | 'recon';
  encompass_loan_id: string | null;
  lo_user_id: string | null;
  lo_name: string | null;
  joined_at: string | null;
  finalized_at: string | null;
}

export function scoreboardVolume(loans: ScoreboardLoan[]): number {
  return loans.reduce((sum, loan) => sum + (loan.trans_details_loan_amt || 0), 0);
}


function cleanScoreboardName(name: string | null | undefined): string {
  return (name || '').replace(/\s+/g, ' ').trim();
}

/** Same finalized-payee selection and fail-closed completeness gate for screen and email. */
export function buildScoreboardAttribution(
  fundedLoans: Pick<ScoreboardLoan, 'encompass_loan_id'>[],
  attributionRows: LOScoreboardAttributionRow[] | null
) {
  const userById = new Map<string, { name: string; createdAt: string | null }>();
  const joinedByUserId = new Map<string, string>();
  const joinedByName = new Map<string, string>();
  const reconData: BankerReconRow[] = [];
  let attributionUserRows = 0;

  attributionRows?.forEach((row) => {
    const name = cleanScoreboardName(row.lo_name);
    if (row.record_type === 'user' && row.lo_user_id && name) {
      attributionUserRows += 1;
    }

    if (row.lo_user_id && name) {
      userById.set(row.lo_user_id, { name, createdAt: row.joined_at || null });
      if (row.joined_at) {
        joinedByUserId.set(row.lo_user_id, row.joined_at);
        joinedByName.set(normalizeLoKey(name), row.joined_at);
      }
    }

    if (row.record_type === 'recon') {
      reconData.push({
        encompass_loan_id: row.encompass_loan_id,
        lo_user_id: row.lo_user_id,
        lo_name: name || null,
        joined_at: row.joined_at || null,
        finalized_at: row.finalized_at
      });
    }
  });

  // Scope attribution + failure checks to the report's funded-loan universe
  // so unrelated visible recons can never skew or false-positive the result.
  const fundedEncompassIds = new Set<string>();
  fundedLoans.forEach((loan) => {
    if (loan.encompass_loan_id) {
      fundedEncompassIds.add(loan.encompass_loan_id);
    }
  });

  // Pick the winning FINALIZED recon per funded loan (latest finalized wins),
  // THEN resolve its payee user — so a later un-resolvable recon never lets a
  // stale row win. Only finalized recons credit production (matches the team's
  // "finalized recons" basis); a draft recon does not move the rankings.
  const winnerByLoan = new Map<string, BankerReconRow>();
  reconData.forEach((recon) => {
    const eid = recon.encompass_loan_id;
    if (!eid || !recon.finalized_at || !fundedEncompassIds.has(eid)) {
      return;
    }
    const prev = winnerByLoan.get(eid);
    if (!prev || (recon.finalized_at || '') > (prev.finalized_at || '')) {
      winnerByLoan.set(eid, recon);
    }
  });

  // A funded loan that HAS a finalized recon but whose payee can't be resolved
  // (missing lo_user_id, deleted user, or `users` RLS hiding it) would silently
  // mis-credit to the unreliable Encompass name — count those as failures.
  const payeeByEncompassId = new Map<string, ReconPayee>();
  let incompleteAttribution = 0;
  winnerByLoan.forEach((winner, eid) => {
    if (!winner.lo_user_id) {
      incompleteAttribution += 1;
      return;
    }
    const name = cleanScoreboardName(winner.lo_name || userById.get(winner.lo_user_id)?.name);
    if (!name) {
      incompleteAttribution += 1;
      return;
    }
    payeeByEncompassId.set(eid, { userId: winner.lo_user_id, name });
  });

  const noAttributionUsers = fundedLoans.length > 0 && attributionUserRows === 0;

  return {
    context: { payeeByEncompassId, joinedByUserId, joinedByName, aliasMap: LO_ALIASES },
    noAttributionUsers, incompleteAttribution, reconCount: reconData.length
  };
}
